<script>
import { showModal } from "@app/store/modalStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import {
    eventGroupState,
    isSelectedEventGroupLocked,
} from "@app/store/eventGroupStore.svelte.js";
import { authState } from "@app/store/authStore.svelte.js";
import { can } from "@app/lib/entitlements.js";
import { environment } from "@app/store/envStore.svelte.js";
import { canGenerateFilmstrip } from "@app/lib/filmstrip.js";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import { isAudioGroupType } from "@app/lib/eventGroupTypes.js";
import { canTranscribeMedia } from "@app/lib/player/mediaSources.js";
import { toast } from "@zerodevx/svelte-toast";
import Menu from "../menu/Menu.svelte";
import MenuItem from "../menu/MenuItem.svelte";
import MenuDivider from "../menu/MenuDivider.svelte";
import * as m from "@app/paraglide/messages.js";

let isLocked = $derived(isSelectedEventGroupLocked());
let hasMediaPath = $derived(Boolean(projectState.media.path));
let canTranscribe = $derived(canTranscribeMedia(projectState.media));
let canLocalMedia = $derived(
    hasMediaPath &&
        (projectState.media.storage === "Local Storage" ||
            projectState.media.storage === "Cloud Storage"),
);
let hasLocalPath = $derived(Boolean(projectState.media.localPath));
let isAdGroup = $derived(
    isAudioGroupType(eventGroupState[projectState.selected]?.type),
);

function promptFitThenRender({ selectAll = false } = {}) {
    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    Swal.fire({
        ...SWAL_BASE,
        customClass: SWAL_CLASSES.confirmDenyCancel,
        title: m.are_you_sure(),
        text: m.render_all_fit_prompt_text(),
        icon: "warning",
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: m.render_all_fit_confirm(),
        denyButtonText: m.render_all_fit_deny(),
        cancelButtonText: m.cancel(),
    }).then((result) => {
        if (result.isConfirmed || result.isDenied) {
            if (selectAll) {
                eventGroupState[projectState.selected].selected = eventGroupState[
                    projectState.selected
                ].events.map((event, count) => {
                    return count;
                });
            }

            showModal("renderAudio", { fitToDuration: result.isConfirmed });
        }
    });
}
</script>

<Menu id="aiTools" label={m.ai_tools()}>
    {#if can("batch") && authState.type === "default" && authState.status !== "in_trial" && environment.online}
        <MenuItem
            label={m.batch_transcription()}
            icon="bi-collection-play"
            id="batchTranscriptionBtn"
            onSelect={() => showModal("batchTranscription")} />
        <MenuDivider />
    {/if}
    {#if environment.online}
        <MenuItem
            label={m.automatic_transcription()}
            icon="bi-mic"
            disabled={!canTranscribe}
            onSelect={() => showModal("automaticTranscription")} />
    {/if}
    {#if can("translation") && environment.online}
        <MenuItem
            label={m.automatic_translation()}
            icon="bi-translate"
            disabled={!eventGroupState[projectState.selected]}
            onSelect={() => showModal("automaticTranslation")} />
    {/if}
    {#if authState.ad && environment.electron && environment.online}
        <MenuItem
            label={m.automatic_ad()}
            icon="bi-eye"
            disabled={!canLocalMedia}
            onSelect={() => showModal("automaticAd")} />
    {/if}
    {#if can("shotDescriptions") && environment.electron && environment.online}
        <MenuItem
            label={m.automatic_scene_descriptions()}
            icon="bi-camera-reels"
            disabled={!canLocalMedia}
            onSelect={() => showModal("automaticSceneDescriptions")} />
    {/if}
    {#if environment.electron}
        <MenuDivider />
        <MenuItem
            label={m.detect_shot_changes()}
            icon="bi-film"
            disabled={!hasLocalPath}
            onSelect={() => showModal("sceneChangeDetection")} />
        <MenuItem
            label={m.ocr_media()}
            icon="bi-scan"
            disabled={!hasLocalPath}
            onSelect={() => showModal("ocrMedia")} />
        <MenuItem
            label={m.pre_render_frame_cache()}
            icon="bi-images"
            disabled={!canGenerateFilmstrip(projectState)}
            onSelect={() => showModal("preRenderFrameCache")} />
        {#if environment.windows}
            <MenuDivider />
            <MenuItem
                label={m.offline_transcription()}
                icon="bi-hdd"
                disabled={!hasLocalPath}
                onSelect={() => showModal("offlineTranscription")} />
            <MenuItem
                label={m.offline_sync()}
                icon="bi-arrow-repeat"
                disabled={!hasLocalPath}
                onSelect={() => showModal("offlineSync")} />
        {/if}
    {/if}
    {#if (authState.ad && environment.online) || environment.adLicense}
        <MenuDivider />
        <MenuItem
            label={m.force_render_audio()}
            icon="bi-volume-up"
            id="forceRenderAudioBtn"
            shortcut="Render Text-to-Speech"
            disabled={!isAdGroup || isLocked}
            disabledReason={isLocked ? m.event_group_locked_toast() : ""}
            onSelect={() => promptFitThenRender({ selectAll: false })} />
        <MenuItem
            label={m.force_render_all_audio()}
            icon="bi-volume-up-fill"
            id="forceRenderAllAudioBtn"
            disabled={!isAdGroup || isLocked}
            disabledReason={isLocked ? m.event_group_locked_toast() : ""}
            onSelect={() => promptFitThenRender({ selectAll: true })} />
    {/if}
    {#if environment.online}
        <MenuDivider />
        <MenuItem
            label={m.transcription_import()}
            icon="bi-file-text"
            id="aiTranscriptImportBtn"
            onSelect={() => showModal("aiTranscriptImport")} />
        {#if can("translation")}
            <MenuItem
                label={m.translation_import()}
                icon="bi-translate"
                onSelect={() => showModal("aiTranslationImport")} />
        {/if}
        {#if authState.ad}
            <MenuItem
                label={m.ad_import_dashboard()}
                icon="bi-eye"
                onSelect={() => showModal("aiAdImport")} />
        {/if}
        {#if can("shotDescriptions")}
            <MenuItem
                label={m.scene_description_import_dashboard()}
                icon="bi-camera-reels"
                onSelect={() => showModal("aiSceneDescriptionImport")} />
        {/if}
    {/if}
</Menu>
